var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// schemas/auth.ts
import { z } from "zod";
import tlds from "tlds";

// validation.ts
var FIELD_LIMITS = {
  name: 30,
  email: 254,
  city: 60,
  schoolName: 80,
  zipCode: 8,
  relation: 50,
  disability: 200,
  referralExtra: 100,
  longText: 2e3,
  url: 2048,
  aidAmount: 6
};
function straightenApostrophes(value) {
  return value.replace(/[\u2018\u2019\u02BC]/g, "'");
}
__name(straightenApostrophes, "straightenApostrophes");
function capitalizeFirstLetter(value) {
  return value.replace(/\p{L}/u, (letter) => letter.toLocaleUpperCase());
}
__name(capitalizeFirstLetter, "capitalizeFirstLetter");

// messages.ts
var REQUIRED = "This field is required";
var INVALID_PHONE = "Please enter a valid phone number";
var MISSING_PHONE_COUNTRY = "Please enter a country code";
var MAX_2000 = "Please shorten this text to 2000 characters or fewer";
var MIN_DEFAULT = /* @__PURE__ */ __name((key, n) => `${key} must be at least ${n} characters`, "MIN_DEFAULT");
var MAX_DEFAULT = /* @__PURE__ */ __name((key, n) => `${key} must be at most ${n} characters`, "MAX_DEFAULT");

// schemas/auth.ts
var TLD_SET = new Set(tlds.map((t) => t.toLowerCase()));
function hasValidTld(email2) {
  const tld = email2.split(".").pop()?.toLowerCase();
  return TLD_SET.has(tld ?? "");
}
__name(hasValidTld, "hasValidTld");
var email = z.string().trim().toLowerCase().max(FIELD_LIMITS.email, MAX_DEFAULT("Email", FIELD_LIMITS.email)).email("Please enter a valid email address").refine(hasValidTld, "Please check your email - the domain appears to be invalid");
var StartOtpSchema = z.object({ email });
var VerifyOtpSchema = z.object({
  email,
  code: z.string().trim().length(4)
});

// schemas/env.ts
import { z as z2 } from "zod";
var RawApiEnvSchema = z2.object({
  // Required on purpose: it controls cookie security flags and the dev OTP
  // bypass, so a missing or misspelled value must fail at boot, never
  // silently fall back to development.
  APP_ENV: z2.enum(["development", "production"]),
  PORT: z2.coerce.number().default(3e3),
  HOST: z2.string().default("0.0.0.0"),
  APP_URL: z2.url().optional(),
  // App version for cache-busting / forcing client refresh on deploy
  APP_VERSION: z2.string().optional(),
  MONGODB_URI: z2.string().min(1),
  CORS_ORIGIN: z2.string().default("http://localhost:5173"),
  // JWT secret (support both names)
  JWT_SECRET: z2.string().min(32).optional(),
  APP_SECRET_KEY: z2.string().min(32).optional(),
  // Twilio (support both Verify service vars)
  TWILIO_ACCOUNT_SID: z2.string().regex(/^AC[A-Za-z0-9]{32}$/, "TWILIO_ACCOUNT_SID must start with AC"),
  TWILIO_SERVICE_SID: z2.string().regex(/^VA[A-Za-z0-9]{32}$/, "TWILIO_SERVICE_SID must start with VA"),
  TWILIO_AUTH_TOKEN: z2.string().min(32),
  // Optional override for the SMS sender. Either an E.164 phone number or a
  // Messaging Service SID (MG...). When unset, the API discovers the account's
  // single SMS-capable number at runtime.
  TWILIO_SMS_FROM: z2.string().regex(/^(\+[1-9]\d{6,14}|MG[A-Za-z0-9]{32})$/, "TWILIO_SMS_FROM must be E.164 or an MG... SID").optional(),
  // Optional integrations
  RECAPTCHA_V3_SECRET: z2.string().optional(),
  // URL used to verify reCAPTCHA tokens (Google's v3 verify endpoint by default)
  RECAPTCHA_VERIFY_URL: z2.url().optional(),
  // Backblaze
  BACKBLAZE_KEY_ID: z2.string(),
  BACKBLAZE_KEY_NAME: z2.string(),
  BACKBLAZE_APPLICATION_KEY: z2.string(),
  BACKBLAZE_BUCKET_ID: z2.string(),
  BACKBLAZE_DOWNLOAD_URL: z2.url(),
  HUBSPOT_CLIENT_TOKEN: z2.string(),
  // Private app client secret: used by HubSpot to sign inbound webhooks
  // (X-HubSpot-Signature-v3). Distinct from the access token above.
  HUBSPOT_CLIENT_SECRET: z2.string().min(1).optional(),
  // Google OAuth (optional)
  GOOGLE_CLIENT_ID: z2.string().optional(),
  GOOGLE_CLIENT_SECRET: z2.string().optional(),
  GOOGLE_CALLBACK_URL: z2.string().optional(),
  SENTRY_AUTH_TOKEN: z2.string(),
  // Sentry runtime/build variables (optional)
  SENTRY_DSN: z2.string().optional(),
  SENTRY_ENV: z2.string().optional(),
  SENTRY_RELEASE: z2.string().optional(),
  SENTRY_TRACES_SAMPLE_RATE: z2.string().optional(),
  SENTRY_CAPTURE_DEV: z2.string().optional(),
  SENTRY_ORG: z2.string().optional(),
  SENTRY_PROJECT: z2.string().optional()
}).superRefine((v, ctx) => {
  if (!v.JWT_SECRET && !v.APP_SECRET_KEY) {
    ctx.addIssue({ code: "custom", message: "Provide JWT_SECRET or APP_SECRET_KEY" });
  }
});
var WebEnvSchema = z2.object({
  VITE_GTM_ID: z2.string().optional(),
  VITE_API_URL: z2.url(),
  VITE_PUBLIC_APP_URL: z2.url().optional(),
  VITE_APP_ENV: z2.string().optional(),
  VITE_RECAPTCHA_V3_KEY: z2.string().optional(),
  VITE_HUBSPOT_CLIENT_TOKEN: z2.string().optional(),
  VITE_HUBSPOT_REDIRECT_ENABLED: z2.string().optional(),
  VITE_HUBSPOT_REDIRECT_URL: z2.url().optional(),
  VITE_TUITION_THIRD_LABEL: z2.string().optional(),
  VITE_TUITION_THIRD_URL: z2.string().optional(),
  VITE_TUITION_ONLINE_SOLD_OUT: z2.enum(["true", "false"]).optional(),
  VITE_TUITION_SF_SOLD_OUT: z2.enum(["true", "false"]).optional(),
  VITE_TUITION_NEXT_YEAR_SOLD_OUT: z2.enum(["true", "false"]).optional(),
  VITE_TUITION_SHOW_THIRD: z2.enum(["true", "false"]).optional(),
  // Optional Sentry client keys
  VITE_SENTRY_DSN: z2.string().optional(),
  VITE_SENTRY_ENV: z2.string().optional(),
  VITE_SENTRY_RELEASE: z2.string().optional(),
  VITE_SENTRY_TRACES_SAMPLE_RATE: z2.string().optional()
});
function parseWebEnv(input) {
  return WebEnvSchema.parse(input);
}
__name(parseWebEnv, "parseWebEnv");
function parseApiEnv(input) {
  const raw = RawApiEnvSchema.parse(input);
  const defaultVersion = raw.APP_ENV === "development" ? "dev" : `v${Date.now()}`;
  return {
    APP_ENV: raw.APP_ENV,
    PORT: raw.PORT,
    HOST: raw.HOST,
    APP_URL: raw.APP_URL ?? `http://localhost:${raw.PORT}`,
    APP_VERSION: raw.APP_VERSION ?? defaultVersion,
    MONGODB_URI: raw.MONGODB_URI,
    CORS_ORIGIN: raw.CORS_ORIGIN,
    JWT_SECRET: raw.JWT_SECRET ?? raw.APP_SECRET_KEY,
    TWILIO_ACCOUNT_SID: raw.TWILIO_ACCOUNT_SID,
    TWILIO_AUTH_TOKEN: raw.TWILIO_AUTH_TOKEN,
    TWILIO_SERVICE_SID: raw.TWILIO_SERVICE_SID,
    TWILIO_SMS_FROM: raw.TWILIO_SMS_FROM,
    RECAPTCHA_V3_SECRET: raw.RECAPTCHA_V3_SECRET,
    RECAPTCHA_VERIFY_URL: raw.RECAPTCHA_VERIFY_URL,
    BACKBLAZE_KEY_ID: raw.BACKBLAZE_KEY_ID,
    BACKBLAZE_KEY_NAME: raw.BACKBLAZE_KEY_NAME,
    BACKBLAZE_APPLICATION_KEY: raw.BACKBLAZE_APPLICATION_KEY,
    BACKBLAZE_BUCKET_ID: raw.BACKBLAZE_BUCKET_ID,
    BACKBLAZE_DOWNLOAD_URL: raw.BACKBLAZE_DOWNLOAD_URL,
    HUBSPOT_CLIENT_TOKEN: raw.HUBSPOT_CLIENT_TOKEN,
    HUBSPOT_CLIENT_SECRET: raw.HUBSPOT_CLIENT_SECRET,
    GOOGLE_CLIENT_ID: raw.GOOGLE_CLIENT_ID,
    GOOGLE_CLIENT_SECRET: raw.GOOGLE_CLIENT_SECRET,
    GOOGLE_CALLBACK_URL: raw.GOOGLE_CALLBACK_URL,
    SENTRY_AUTH_TOKEN: raw.SENTRY_AUTH_TOKEN,
    SENTRY_DSN: raw.SENTRY_DSN,
    SENTRY_ENV: raw.SENTRY_ENV,
    SENTRY_RELEASE: raw.SENTRY_RELEASE,
    SENTRY_TRACES_SAMPLE_RATE: raw.SENTRY_TRACES_SAMPLE_RATE,
    SENTRY_CAPTURE_DEV: raw.SENTRY_CAPTURE_DEV,
    SENTRY_ORG: raw.SENTRY_ORG,
    SENTRY_PROJECT: raw.SENTRY_PROJECT
  };
}
__name(parseApiEnv, "parseApiEnv");

// schemas/user/db-user.ts
import { z as z3 } from "zod";
var ApplicationProcessValues = [
  "Draft",
  "Submitted",
  "Admitted",
  "Not Accepted"
];
var defaultApplicationStatus = /* @__PURE__ */ __name(() => ({
  general: false,
  background: false,
  program: false,
  aid: false
}), "defaultApplicationStatus");
var defaultApplicationProcess = "Draft";
var WorkflowProcessStatuses = ["Admitted", "Not Accepted"];
var ContactTypes = [
  "Teenager",
  "Parent",
  "Advisor/Counselor",
  "Mentor",
  "Teacher",
  "Partner"
];
var DbUserSchema = z3.object({
  _id: z3.string(),
  createdAt: z3.string(),
  updatedAt: z3.string(),
  email: z3.string(),
  name: z3.string().nullable().optional(),
  last_name: z3.string().nullable().optional(),
  type: z3.string().nullable().optional(),
  born_date: z3.string().nullable().optional(),
  age: z3.number().nullable(),
  gender: z3.string().nullable().optional(),
  country: z3.string().nullable().optional(),
  city: z3.string().nullable().optional(),
  zip_code: z3.string().nullable().optional(),
  phone: z3.string().nullable().optional(),
  has_disability: z3.boolean().nullable().optional(),
  disability: z3.string().nullable().optional(),
  school_name: z3.string().nullable().optional(),
  school_country: z3.string().nullable().optional(),
  school_city: z3.string().nullable().optional(),
  school_zip_code: z3.string().nullable().optional(),
  school_summer_period: z3.string().nullable().optional(),
  parent_name: z3.string().nullable().optional(),
  parent_last_name: z3.string().nullable().optional(),
  parent_email: z3.string().nullable().optional(),
  parent_phone: z3.string().nullable().optional(),
  parent_type_relation: z3.string().nullable().optional(),
  parent_relation: z3.string().nullable().optional(),
  share_proud: z3.string().nullable().optional(),
  share_reason_to_apply: z3.string().nullable().optional(),
  video_intro: z3.string().nullable().optional(),
  video_intro_is_public: z3.boolean().nullable().optional(),
  share_what_want_to_learn: z3.string().nullable().optional(),
  share_practical_skills: z3.string().nullable().optional(),
  share_extracurricular_activities: z3.string().nullable().optional(),
  share_entrepreneurial_type: z3.string().nullable().optional(),
  share_what_makes_teenager: z3.string().nullable().optional(),
  share_how_hear_about_us: z3.string().nullable().optional(),
  share_how_hear_about_us_extra: z3.string().nullable().optional(),
  share_something_to_know_about: z3.string().nullable().optional(),
  program_name: z3.string().nullable().optional(),
  priority_choice: z3.string().nullable().optional(),
  summer_commitments: z3.string().nullable().optional(),
  is_required_aid: z3.boolean().nullable().optional(),
  aid_amount: z3.string().nullable().optional(),
  aid_currency: z3.string().nullable().optional(),
  aid_file_id: z3.string().nullable().optional(),
  aid_is_terms_accepted: z3.boolean().nullable().optional(),
  file_path: z3.string().nullable().optional(),
  file_original: z3.string().nullable().optional(),
  file_mime: z3.string().nullable().optional(),
  file_size: z3.number().nullable().optional(),
  hubspot_id: z3.string().nullable(),
  contact_type: z3.union([z3.string(), z3.null()]).nullable().optional(),
  application_process: z3.enum(ApplicationProcessValues),
  application_status: z3.object({
    general: z3.boolean(),
    background: z3.boolean(),
    program: z3.boolean(),
    aid: z3.boolean()
  }),
  lastTimeSaved: z3.number().nullable().optional()
});

// schemas/user/aid/index.ts
import { z as z6 } from "zod";

// schemas/user/aid/aid.ts
import { z as z4 } from "zod";
var aidSchema = z4.object({
  is_required_aid: z4.boolean().optional().refine((v) => v === true || v === false, {
    message: "Please choose Yes or No"
  }),
  amount: z4.string().max(FIELD_LIMITS.aidAmount, MAX_DEFAULT("Financial aid amount", FIELD_LIMITS.aidAmount)).nullable(),
  currency: z4.string().nullable(),
  file_id: z4.string().nullable(),
  is_terms_accepted: z4.boolean().nullable()
}).superRefine((data, ctx) => {
  if (data.is_required_aid) {
    const amount = data.amount;
    if (!amount) {
      ctx.addIssue({ code: "custom", message: "The financial aid amount is required", path: ["amount"] });
    } else if (!/^\d+$/.test(amount)) {
      ctx.addIssue({ code: "custom", message: "Enter a valid amount (>= 100)", path: ["amount"] });
    } else {
      const v = Number(amount);
      if (!Number.isFinite(v) || v < 100) {
        ctx.addIssue({ code: "custom", message: "Enter a valid amount (>= 100)", path: ["amount"] });
      }
      if (v > 1e5) {
        ctx.addIssue({ code: "custom", message: "You are not eligible to apply for financial need support.", path: ["amount"] });
      }
    }
    if (!data.currency) {
      ctx.addIssue({ code: "custom", message: "Currency is required when is_required is true", path: ["currency"] });
    }
    if (data.is_terms_accepted !== true) {
      ctx.addIssue({ code: "custom", message: "Must be checked to continue", path: ["is_terms_accepted"] });
    }
    if (!data.file_id) {
      ctx.addIssue({ code: "custom", message: "File evidence is required when financial aid is required", path: ["file_id"] });
    }
  }
});

// schemas/user/aid/file.ts
import { z as z5 } from "zod";
var fileSchema = z5.object({
  path: z5.string().nullable(),
  original: z5.string().nullable(),
  mime: z5.string().nullable(),
  size: z5.number().nullable()
});

// schemas/user/aid/index.ts
var stepAidSchema = z6.object({
  aid: aidSchema,
  file: fileSchema.optional()
});

// schemas/user/background/background.ts
import { z as z7 } from "zod";

// schemas/referral.ts
var REFERRAL_OPTIONS = [
  "Google Search",
  "Social Media",
  "Leangap alumni or mentor",
  "Friend or family member",
  "College counselor",
  "Online article",
  "AI search or chatbot",
  "Other"
];
var REFERRAL_NEEDS_EXTRA = [
  "Social Media",
  "Leangap alumni or mentor",
  "Friend or family member",
  "College counselor",
  "AI search or chatbot",
  "Other"
];
var REFERRAL_EXTRA_LABELS = {
  "Leangap alumni or mentor": "Name of the alumni or mentor",
  "College counselor": "Which organization does your college counselor work for?",
  "Friend or family member": "Name of friend/family",
  "AI search or chatbot": "Which AI did you use?",
  Other: "Mention the specific source"
};
var REFERRAL_NEEDS_EXTRA_SET = new Set(
  REFERRAL_NEEDS_EXTRA.map((s) => String(s).trim())
);
function needsExtraFor(value) {
  const v = (value ?? "").trim();
  return REFERRAL_NEEDS_EXTRA_SET.has(v);
}
__name(needsExtraFor, "needsExtraFor");

// schemas/user/background/background.ts
var URL_REGEX = /^https?:\/\/.+/i;
var VIDEO_INTRO_REQUIRED_MSG = "Video introduction is required";
var VIDEO_INTRO_URL_MSG = "Please enter a valid URL (e.g., https://youtube.com/...)";
var VIDEO_INTRO_PUBLIC_REQUIRED_MSG = "Must be checked to continue";
var baseBackgroundFields = {
  share_proud: z7.string().nonempty(REQUIRED).max(FIELD_LIMITS.longText, MAX_2000),
  share_reason_to_apply: z7.string().nonempty(REQUIRED).max(FIELD_LIMITS.longText, MAX_2000),
  video_intro: z7.string().max(FIELD_LIMITS.url, MAX_DEFAULT("Video URL", FIELD_LIMITS.url)).refine((val) => !val || URL_REGEX.test(val), VIDEO_INTRO_URL_MSG).optional().nullable(),
  video_intro_is_public: z7.boolean().optional().nullable(),
  share_what_want_to_learn: z7.string().nonempty(REQUIRED).max(FIELD_LIMITS.longText, MAX_2000),
  share_practical_skills: z7.string().nonempty(REQUIRED).max(FIELD_LIMITS.longText, MAX_2000),
  share_extracurricular_activities: z7.string().nonempty(REQUIRED).max(FIELD_LIMITS.longText, MAX_2000),
  share_entrepreneurial_type: z7.string().nonempty(REQUIRED),
  share_what_makes_teenager: z7.string().nonempty(REQUIRED).max(FIELD_LIMITS.longText, MAX_2000),
  share_how_hear_about_us: z7.string().nonempty(REQUIRED),
  share_how_hear_about_us_extra: z7.string().max(FIELD_LIMITS.referralExtra, MAX_DEFAULT("Referral source", FIELD_LIMITS.referralExtra)).optional(),
  share_something_to_know_about: z7.string().max(FIELD_LIMITS.longText, MAX_2000).optional().nullable()
};
function createBackgroundSchema(requireVideoIntro) {
  return z7.object(baseBackgroundFields).superRefine((data, ctx) => {
    const main = data.share_how_hear_about_us.trim();
    const extra = (data.share_how_hear_about_us_extra ?? "").trim();
    if (REFERRAL_NEEDS_EXTRA_SET.has(main)) {
      if (!extra) {
        ctx.addIssue({
          code: "custom",
          message: REQUIRED,
          path: ["share_how_hear_about_us_extra"]
        });
      } else if (extra.length < 2) {
        ctx.addIssue({
          code: "custom",
          message: MIN_DEFAULT("Referral source", 2),
          path: ["share_how_hear_about_us_extra"]
        });
      }
    }
    if (requireVideoIntro) {
      const videoIntro = (data.video_intro ?? "").trim();
      if (!videoIntro) {
        ctx.addIssue({
          code: "custom",
          message: VIDEO_INTRO_REQUIRED_MSG,
          path: ["video_intro"]
        });
      }
      if (data.video_intro_is_public !== true) {
        ctx.addIssue({
          code: "custom",
          message: VIDEO_INTRO_PUBLIC_REQUIRED_MSG,
          path: ["video_intro_is_public"]
        });
      }
    }
  });
}
__name(createBackgroundSchema, "createBackgroundSchema");
var backgroundSchema = createBackgroundSchema(false);

// schemas/user/countries.ts
var ENGLISH_SPEAKING_COUNTRIES = [
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "New Zealand",
  "Ireland",
  "South Africa"
];
function requiresVideoIntro(country) {
  if (!country) {
    return false;
  }
  return !ENGLISH_SPEAKING_COUNTRIES.includes(country);
}
__name(requiresVideoIntro, "requiresVideoIntro");

// schemas/user/general/index.ts
import { z as z14 } from "zod";

// schemas/user/general/student-info.ts
import { z as z10 } from "zod";

// schemas/phone.ts
import { z as z8 } from "zod";

// utils/phone.ts
import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString
} from "libphonenumber-js";
function isCountryCode(value) {
  return getCountries().includes(value);
}
__name(isCountryCode, "isCountryCode");
function isValidPhone(value) {
  const trimmed = value.trim();
  if (!trimmed.startsWith("+")) return false;
  try {
    return isValidPhoneNumber(trimmed);
  } catch {
    return false;
  }
}
__name(isValidPhone, "isValidPhone");
function parsePhone(value) {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const parsed = parsePhoneNumberFromString(trimmed);
    if (!parsed?.nationalNumber || !parsed.country) return null;
    return {
      country: parsed.country,
      nationalNumber: parsed.nationalNumber,
      e164: parsed.number
    };
  } catch {
    return null;
  }
}
__name(parsePhone, "parsePhone");
function toE164(nationalOrRaw, country) {
  const trimmed = nationalOrRaw.trim();
  if (!trimmed) return "";
  try {
    const international = parsePhoneNumberFromString(trimmed);
    if (international?.number) return international.number;
    const parsed = parsePhoneNumberFromString(trimmed, country);
    if (parsed?.number) return parsed.number;
  } catch {
  }
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";
  return `+${getCountryCallingCode(country)}${digits}`;
}
__name(toE164, "toE164");

// schemas/phone.ts
var phoneNumberSchema = z8.string().trim().nonempty(REQUIRED).superRefine((value, ctx) => {
  if (!value) return;
  if (!value.startsWith("+")) {
    ctx.addIssue({ code: "custom", message: MISSING_PHONE_COUNTRY });
    return;
  }
  if (!isValidPhone(value)) ctx.addIssue({ code: "custom", message: INVALID_PHONE });
});

// schemas/user/general/student-info.ts
import { DateTime as DateTime2 } from "luxon";

// utils/date.ts
import { parseDate, CalendarDate } from "@internationalized/date";
import { DateTime } from "luxon";
function getSummerBoundsCalendarDates(from = DateTime.now()) {
  const { minDate, maxDate } = summerBounds(from);
  return {
    min: new CalendarDate(minDate.year, minDate.month, minDate.day),
    max: new CalendarDate(maxDate.year, maxDate.month, maxDate.day)
  };
}
__name(getSummerBoundsCalendarDates, "getSummerBoundsCalendarDates");
function calendarRangeStringToCalendarDates(rangeStr) {
  if (!rangeStr) return void 0;
  const [startStr, endStr] = rangeStr.split("/");
  if (!startStr || !endStr) return void 0;
  try {
    return {
      start: parseDate(startStr),
      end: parseDate(endStr)
    };
  } catch {
    return void 0;
  }
}
__name(calendarRangeStringToCalendarDates, "calendarRangeStringToCalendarDates");
function calendarDatesToCalendarRangeString(range) {
  if (!range || !range.start || !range.end) return "";
  const toIso = /* @__PURE__ */ __name((d) => `${d.year.toString().padStart(4, "0")}-${d.month.toString().padStart(2, "0")}-${d.day.toString().padStart(2, "0")}`, "toIso");
  return `${toIso(range.start)}/${toIso(range.end)}`;
}
__name(calendarDatesToCalendarRangeString, "calendarDatesToCalendarRangeString");
function nextJune15(from = DateTime.now()) {
  const j15 = DateTime.fromObject({ year: from.year, month: 6, day: 15 });
  return from > j15 ? j15.plus({ years: 1 }) : j15;
}
__name(nextJune15, "nextJune15");
function nextValidSummerYear(from = DateTime.now()) {
  const j15 = DateTime.fromObject({ year: from.year, month: 6, day: 15 });
  return from > j15 ? from.year + 1 : from.year;
}
__name(nextValidSummerYear, "nextValidSummerYear");
function upcomingSummerYear(from = DateTime.now()) {
  return from.month >= 9 ? from.year + 1 : from.year;
}
__name(upcomingSummerYear, "upcomingSummerYear");
function summerBounds(from = DateTime.now()) {
  const y = nextValidSummerYear(from);
  return {
    minDate: DateTime.fromObject({ year: y, month: 1, day: 1 }),
    maxDate: DateTime.fromObject({ year: y, month: 12, day: 31 }),
    year: y
  };
}
__name(summerBounds, "summerBounds");
function parseSchoolDateRange(rangeStr) {
  if (!rangeStr) return null;
  const [startStr, endStr] = rangeStr.split("/");
  const start = DateTime.fromISO(startStr);
  const end = DateTime.fromISO(endStr);
  if (!start.isValid || !end.isValid) return null;
  return { start, end };
}
__name(parseSchoolDateRange, "parseSchoolDateRange");
function mdyToTimestamp(mdy) {
  const m = /^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/.exec(mdy.trim());
  if (!m) return null;
  const month = Number(m[1]);
  const day = Number(m[2]);
  const year = Number(m[3]);
  return Date.UTC(year, month - 1, day);
}
__name(mdyToTimestamp, "mdyToTimestamp");
function ageFromCalendarDate(d) {
  if (!d) return "";
  const today = /* @__PURE__ */ new Date();
  let age = today.getFullYear() - d.year;
  const beforeBirthday = today.getMonth() + 1 < d.month || today.getMonth() + 1 === d.month && today.getDate() < d.day;
  if (beforeBirthday) age -= 1;
  if (age < 0 || age > 120) return "";
  return String(age);
}
__name(ageFromCalendarDate, "ageFromCalendarDate");
var isMDY = /* @__PURE__ */ __name((s) => !!s && /^\d{2}\/\d{2}\/\d{4}$/.test(s), "isMDY");
var isISO = /* @__PURE__ */ __name((s) => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s), "isISO");
function mdyToCalendarDate(mdy) {
  if (!mdy || !isMDY(mdy)) return null;
  const [mm, dd, yyyy] = mdy.split("/");
  try {
    return parseDate(`${yyyy}-${mm}-${dd}`);
  } catch {
    return null;
  }
}
__name(mdyToCalendarDate, "mdyToCalendarDate");
function isoToCalendarDate(iso) {
  if (!iso || !isISO(iso)) return null;
  try {
    return parseDate(iso);
  } catch {
    return null;
  }
}
__name(isoToCalendarDate, "isoToCalendarDate");
function anyToCalendarDate(s) {
  if (!s) return null;
  if (isMDY(s)) return mdyToCalendarDate(s);
  if (isISO(s)) return isoToCalendarDate(s);
  return null;
}
__name(anyToCalendarDate, "anyToCalendarDate");
function calendarDateToMDY(val) {
  if (!val) return "";
  const mm = val.month < 10 ? `0${val.month}` : String(val.month);
  const dd = val.day < 10 ? `0${val.day}` : String(val.day);
  const yyyy = String(val.year);
  return `${mm}/${dd}/${yyyy}`;
}
__name(calendarDateToMDY, "calendarDateToMDY");
function computeAgeFromMDY(mdy) {
  const dt = DateTime.fromFormat(mdy, "MM/dd/yyyy");
  if (!dt.isValid) return "";
  const years = Math.floor(DateTime.now().diff(dt, "years").years);
  return years >= 0 ? String(years) : "";
}
__name(computeAgeFromMDY, "computeAgeFromMDY");

// schemas/person-name.ts
import { z as z9 } from "zod";
var personNameSchema = /* @__PURE__ */ __name((label) => z9.string().trim().transform(capitalizeFirstLetter).superRefine((value, ctx) => {
  if (!value) {
    ctx.addIssue({ code: "custom", message: REQUIRED });
  } else if (value.length < 2) {
    ctx.addIssue({ code: "custom", message: MIN_DEFAULT(label, 2) });
  } else if (value.length > FIELD_LIMITS.name) {
    ctx.addIssue({
      code: "custom",
      message: MAX_DEFAULT(label, FIELD_LIMITS.name)
    });
  }
}), "personNameSchema");

// schemas/user/general/student-info.ts
var studentInfoSchema = z10.object({
  email,
  name: personNameSchema("First name"),
  last_name: personNameSchema("Last name"),
  born_date: z10.string().nonempty(REQUIRED),
  age: z10.string().nonempty(REQUIRED),
  gender: z10.string().nonempty(REQUIRED),
  country: z10.string().nonempty(REQUIRED),
  city: z10.string().nonempty(REQUIRED).min(3, MIN_DEFAULT("City", 3)).max(FIELD_LIMITS.city, MAX_DEFAULT("City", FIELD_LIMITS.city)),
  zip_code: z10.string().nonempty(REQUIRED).min(5, MIN_DEFAULT("Zip code", 5)).max(FIELD_LIMITS.zipCode, MAX_DEFAULT("Zip code", FIELD_LIMITS.zipCode)),
  phone: phoneNumberSchema
}).superRefine((data, ctx) => {
  const birth = DateTime2.fromFormat(data.born_date, "MM/dd/yyyy");
  if (!birth.isValid) {
    ctx.addIssue({ code: "custom", message: "Invalid date format.", path: ["born_date"] });
    return;
  }
  const now = DateTime2.now();
  const j15 = nextJune15(now);
  const ageAtJune15 = Math.floor(j15.diff(birth, "years").years);
  const minAgeByJune15 = 14;
  const maxAgeByJune15 = 17;
  if (ageAtJune15 < minAgeByJune15) {
    ctx.addIssue({
      code: "custom",
      message: `You must be at least ${minAgeByJune15} years old by June 15th to participate`,
      path: ["age"]
    });
  }
  if (ageAtJune15 > maxAgeByJune15) {
    ctx.addIssue({
      code: "custom",
      message: `You must be ${maxAgeByJune15} years old or younger on June 15th to participate. Turning 18 during the program is fine.`,
      path: ["age"]
    });
  }
});

// schemas/user/general/high-school.ts
import { z as z11 } from "zod";
var highSchoolSchema = z11.object({
  name: z11.string().nonempty(REQUIRED).min(2, MIN_DEFAULT("High School name", 2)).max(FIELD_LIMITS.schoolName, MAX_DEFAULT("High School name", FIELD_LIMITS.schoolName)),
  country: z11.string().nonempty(REQUIRED),
  city: z11.string().nonempty(REQUIRED).min(2, MIN_DEFAULT("High School city", 2)).max(FIELD_LIMITS.city, MAX_DEFAULT("High School city", FIELD_LIMITS.city)),
  zip_code: z11.string().nonempty(REQUIRED).min(5, MIN_DEFAULT("High School zip code", 5)).max(FIELD_LIMITS.zipCode, MAX_DEFAULT("High School zip code", FIELD_LIMITS.zipCode)),
  school_summer_period: z11.string().nonempty()
}).superRefine((data, ctx) => {
  const parsed = parseSchoolDateRange(data.school_summer_period);
  if (!parsed) {
    ctx.addIssue({ code: "custom", message: "Unable to parse the provided summer break dates", path: ["school_summer_period"] });
    return;
  }
  const { minDate, maxDate, year } = summerBounds();
  if (parsed.start.year !== year) {
    ctx.addIssue({ code: "custom", message: `The summer break start date must be within the year ${year}`, path: ["school_summer_period"] });
  }
  if (parsed.end.year !== year) {
    ctx.addIssue({ code: "custom", message: `The summer break end date must be within the year ${year}`, path: ["school_summer_period"] });
  }
  if (parsed.start < minDate) {
    ctx.addIssue({ code: "custom", message: `The summer break start date cannot be before ${minDate.toISODate()}.`, path: ["school_summer_period"] });
  }
  if (parsed.end > maxDate) {
    ctx.addIssue({ code: "custom", message: `The summer break end date cannot be after ${maxDate.toISODate()}`, path: ["school_summer_period"] });
  }
  if (parsed.end < parsed.start) {
    ctx.addIssue({ code: "custom", message: "The summer break end date must not be before the start date", path: ["school_summer_period"] });
  }
});

// schemas/user/general/parent.ts
import { z as z12 } from "zod";
var parentSchema = z12.object({
  parent_name: personNameSchema("Parent first name"),
  parent_last_name: personNameSchema("Parent last name"),
  parent_email: email.nonempty(REQUIRED),
  parent_phone: phoneNumberSchema,
  parent_type_relation: z12.string().nonempty(REQUIRED),
  parent_relation: z12.string().max(FIELD_LIMITS.relation, MAX_DEFAULT("Parent relation", FIELD_LIMITS.relation)).optional().nullable()
}).superRefine((data, ctx) => {
  const other = data.parent_type_relation === "Other";
  const rel = data.parent_relation ?? "";
  if (other && rel.length < 2) {
    ctx.addIssue({
      code: "custom",
      message: MIN_DEFAULT("Parent relation", 2),
      path: ["parent_relation"]
    });
  }
});

// schemas/user/general/other.ts
import { z as z13 } from "zod";
var otherSchema = z13.object({
  has_disability: z13.boolean(),
  disability: z13.string().max(FIELD_LIMITS.disability, MAX_DEFAULT("Disability description", FIELD_LIMITS.disability)).optional().nullable()
}).refine(
  (data) => {
    if (data.has_disability) {
      return data.disability != null && data.disability.length >= 2;
    }
    return true;
  },
  {
    message: MIN_DEFAULT("Disability description", 2),
    path: ["disability"]
  }
);

// schemas/user/general/index.ts
function comparablePhone(value) {
  const raw = String(value ?? "").trim();
  if (!raw || !isValidPhone(raw)) return "";
  return parsePhone(raw)?.e164 ?? raw;
}
__name(comparablePhone, "comparablePhone");
var stepGeneralSchema = z14.object({
  studentInfo: studentInfoSchema,
  highSchool: highSchoolSchema,
  parent: parentSchema,
  other: otherSchema
}).refine((data) => {
  try {
    const s = data.studentInfo?.email;
    const p = data.parent?.parent_email;
    if (!s || !p) return true;
    return String(s).trim().toLowerCase() !== String(p).trim().toLowerCase();
  } catch (_) {
    return true;
  }
}, {
  message: "Parent/Guardian email must be different from student email",
  path: ["parent", "parent_email"]
}).refine((data) => {
  const student = comparablePhone(data.studentInfo?.phone);
  const parent = comparablePhone(data.parent?.parent_phone);
  if (!student || !parent) return true;
  return student !== parent;
}, {
  message: "Parent/Guardian phone number must be different from student phone number",
  path: ["parent", "parent_phone"]
});

// schemas/user/program/program.ts
import { z as z15 } from "zod";
var programSchema = z15.object({
  name: z15.string().nonempty(REQUIRED),
  priority_choice: z15.string().nonempty(REQUIRED).max(FIELD_LIMITS.longText, MAX_2000),
  summer_commitments: z15.string().nonempty(REQUIRED).max(FIELD_LIMITS.longText, MAX_2000)
});

// schemas/user/application.ts
import { z as z16 } from "zod";
function computeApplicationStatus(user) {
  const general = stepGeneralSchema.safeParse({
    studentInfo: {
      email: user.email,
      name: user.name,
      last_name: user.last_name,
      born_date: user.born_date,
      // schema expects age as string (MM/dd/yyyy validation uses age as string)
      age: String(user.age ?? ""),
      gender: user.gender,
      country: user.country,
      city: user.city,
      zip_code: user.zip_code,
      phone: user.phone
    },
    highSchool: {
      name: user.school_name,
      country: user.school_country,
      city: user.school_city,
      zip_code: user.school_zip_code,
      school_summer_period: user.school_summer_period
    },
    parent: {
      parent_name: user.parent_name,
      parent_last_name: user.parent_last_name,
      parent_email: user.parent_email,
      parent_phone: user.parent_phone,
      parent_type_relation: user.parent_type_relation,
      parent_relation: user.parent_relation
    },
    other: {
      has_disability: user.has_disability,
      disability: user.disability
    }
  }).success;
  const needsVideoIntro = requiresVideoIntro(user.country);
  const backgroundSchema2 = createBackgroundSchema(needsVideoIntro);
  const background = backgroundSchema2.safeParse({
    // coerce undefined -> null for nullable string fields so zod treats them as
    // intentionally empty (nullable) instead of missing (undefined)
    share_proud: user.share_proud ?? null,
    share_reason_to_apply: user.share_reason_to_apply ?? null,
    video_intro: user.video_intro ?? null,
    video_intro_is_public: user.video_intro_is_public ?? null,
    share_what_want_to_learn: user.share_what_want_to_learn ?? null,
    share_practical_skills: user.share_practical_skills ?? null,
    share_extracurricular_activities: user.share_extracurricular_activities ?? null,
    share_entrepreneurial_type: user.share_entrepreneurial_type ?? null,
    share_what_makes_teenager: user.share_what_makes_teenager ?? null,
    share_how_hear_about_us: user.share_how_hear_about_us ?? null,
    share_how_hear_about_us_extra: user.share_how_hear_about_us_extra ?? null,
    share_something_to_know_about: user.share_something_to_know_about ?? null
  }).success;
  const program = programSchema.safeParse({
    // stepProgramSchema expects all required Program Choice answers
    name: user.program_name,
    priority_choice: user.priority_choice,
    summer_commitments: user.summer_commitments
  }).success;
  const aid = stepAidSchema.safeParse({
    aid: {
      // ensure nullable string fields are explicit null when missing so zod
      // validators that accept null (but not undefined) behave correctly
      is_required_aid: user.is_required_aid,
      amount: user.aid_amount ?? null,
      currency: user.aid_currency ?? null,
      file_id: user.aid_file_id ?? null,
      is_terms_accepted: user.aid_is_terms_accepted ?? null
    },
    file: {
      path: user.file_path ?? null,
      original: user.file_original ?? null,
      mime: user.file_mime ?? null,
      size: user.file_size ?? null
    }
  }).success;
  return {
    general,
    background,
    program,
    aid
  };
}
__name(computeApplicationStatus, "computeApplicationStatus");
var isApplicationComplete = /* @__PURE__ */ __name((s) => s.general && s.background && s.program && s.aid, "isApplicationComplete");
var limitedUpdateString = /* @__PURE__ */ __name((label, max) => z16.string().max(max, MAX_DEFAULT(label, max)).nullable().optional(), "limitedUpdateString");
var applicationUpdateSchema = z16.object({
  // student
  email: email.optional(),
  name: personNameSchema("First name").nullable().optional(),
  last_name: personNameSchema("Last name").nullable().optional(),
  born_date: z16.string().nullable().optional(),
  age: z16.number().nullable().optional(),
  gender: z16.string().nullable().optional(),
  country: z16.string().nullable().optional(),
  city: limitedUpdateString("City", FIELD_LIMITS.city),
  zip_code: limitedUpdateString("Zip code", FIELD_LIMITS.zipCode),
  phone: z16.string().nullable().optional(),
  // high school
  school_name: limitedUpdateString("High School name", FIELD_LIMITS.schoolName),
  school_country: z16.string().nullable().optional(),
  school_city: limitedUpdateString("High School city", FIELD_LIMITS.city),
  school_zip_code: limitedUpdateString("High School zip code", FIELD_LIMITS.zipCode),
  school_summer_period: z16.string().nullable().optional(),
  // parent
  parent_name: personNameSchema("Parent first name").nullable().optional(),
  parent_last_name: personNameSchema("Parent last name").nullable().optional(),
  parent_email: email.nullable().optional(),
  parent_phone: z16.string().nullable().optional(),
  parent_type_relation: z16.string().nullable().optional(),
  parent_relation: limitedUpdateString("Parent relation", FIELD_LIMITS.relation),
  // other
  has_disability: z16.boolean().nullable().optional(),
  disability: limitedUpdateString("Disability description", FIELD_LIMITS.disability),
  // background
  share_proud: limitedUpdateString("Proudest achievement", FIELD_LIMITS.longText),
  share_reason_to_apply: limitedUpdateString("Reason to apply", FIELD_LIMITS.longText).transform(
    (v) => v == null ? v : straightenApostrophes(v)
  ),
  video_intro: limitedUpdateString("Video URL", FIELD_LIMITS.url),
  video_intro_is_public: z16.boolean().nullable().optional(),
  share_what_want_to_learn: limitedUpdateString("Learning goal", FIELD_LIMITS.longText),
  share_practical_skills: limitedUpdateString("Practical skills", FIELD_LIMITS.longText),
  share_extracurricular_activities: limitedUpdateString(
    "Extracurricular activities",
    FIELD_LIMITS.longText
  ),
  share_entrepreneurial_type: z16.string().nullable().optional(),
  share_what_makes_teenager: limitedUpdateString("Unique teenager response", FIELD_LIMITS.longText),
  share_how_hear_about_us: z16.string().nullable().optional(),
  share_how_hear_about_us_extra: limitedUpdateString("Referral source", FIELD_LIMITS.referralExtra),
  share_something_to_know_about: limitedUpdateString(
    "Additional information",
    FIELD_LIMITS.longText
  ),
  // program
  program_name: z16.string().nullable().optional(),
  priority_choice: limitedUpdateString("Priority choice", FIELD_LIMITS.longText),
  summer_commitments: limitedUpdateString("Summer commitments", FIELD_LIMITS.longText),
  // aid
  is_required_aid: z16.boolean().nullable().optional(),
  aid_amount: limitedUpdateString("Financial aid amount", FIELD_LIMITS.aidAmount),
  aid_currency: z16.string().nullable().optional(),
  aid_file_id: z16.string().nullable().optional(),
  aid_is_terms_accepted: z16.boolean().nullable().optional(),
  // file
  file_path: z16.string().nullable().optional(),
  file_original: z16.string().nullable().optional(),
  file_mime: z16.string().nullable().optional(),
  file_size: z16.number().nullable().optional(),
  // application status
  application_status: z16.record(z16.string(), z16.boolean()).optional()
});

// schemas/parent.ts
import { z as z17 } from "zod";
var parentRegistrationSchema = z17.object({
  firstName: personNameSchema("First name"),
  lastName: personNameSchema("Last name"),
  email,
  phoneNumber: phoneNumberSchema,
  country: z17.string().min(2, MIN_DEFAULT("Country", 2)),
  city: z17.string().min(2, MIN_DEFAULT("City", 2)).max(FIELD_LIMITS.city, MAX_DEFAULT("City", FIELD_LIMITS.city)),
  zipCode: z17.string().min(5, MIN_DEFAULT("Zip code", 5)).max(FIELD_LIMITS.zipCode, MAX_DEFAULT("Zip code", FIELD_LIMITS.zipCode)),
  numberOfChildren: z17.enum(["Only child", "Two children", "More than three"]),
  youngestChildGradYear: z17.string().regex(/^\d{4}$/, "Please enter a valid 4-digit year").refine((y) => +y >= 2025 && +y <= 2030, "Graduation year must be between 2025 and 2030"),
  referralSource: z17.string().min(2, "Referral source is required"),
  referralSourceExtra: z17.string().max(FIELD_LIMITS.referralExtra, MAX_DEFAULT("Referral source", FIELD_LIMITS.referralExtra)).optional(),
  additionalComments: z17.string().max(FIELD_LIMITS.longText, MAX_DEFAULT("Additional comments", FIELD_LIMITS.longText)).optional()
}).superRefine((data, ctx) => {
  const main = data.referralSource?.trim() ?? "";
  const extra = (data.referralSourceExtra ?? "").trim();
  if (REFERRAL_NEEDS_EXTRA_SET.has(main)) {
    if (!extra) {
      ctx.addIssue({
        code: "custom",
        message: REQUIRED,
        path: ["referralSourceExtra"]
      });
    } else if (extra.length < 2) {
      ctx.addIssue({
        code: "custom",
        message: MIN_DEFAULT("Referral source", 2),
        path: ["referralSourceExtra"]
      });
    }
  }
});

// schemas/counselor.ts
import { z as z18 } from "zod";
var counselorRegistrationSchema = z18.object({
  firstName: personNameSchema("First name"),
  lastName: personNameSchema("Last name"),
  email,
  phoneNumber: phoneNumberSchema,
  country: z18.string().min(2, MIN_DEFAULT("Country", 2)),
  city: z18.string().min(2, MIN_DEFAULT("City", 2)).max(FIELD_LIMITS.city, MAX_DEFAULT("City", FIELD_LIMITS.city)),
  zipCode: z18.string().min(5, MIN_DEFAULT("Zip code", 5)).max(FIELD_LIMITS.zipCode, MAX_DEFAULT("Zip code", FIELD_LIMITS.zipCode)),
  numberOfStudents: z18.enum([
    "1-10 students",
    "11-50 students",
    "51-100 students",
    "100+ students"
  ]),
  counselingServiceUrl: z18.string().min(1, "Website URL is required").max(FIELD_LIMITS.url, MAX_DEFAULT("Website URL", FIELD_LIMITS.url)),
  referralSource: z18.string().min(2, "Referral source is required"),
  referralSourceExtra: z18.string().max(FIELD_LIMITS.referralExtra, MAX_DEFAULT("Referral source", FIELD_LIMITS.referralExtra)).optional(),
  additionalComments: z18.string().max(FIELD_LIMITS.longText, MAX_DEFAULT("Additional comments", FIELD_LIMITS.longText)).optional()
}).superRefine((data, ctx) => {
  const main = data.referralSource?.trim() ?? "";
  const extra = (data.referralSourceExtra ?? "").trim();
  if (REFERRAL_NEEDS_EXTRA_SET.has(main)) {
    if (!extra) {
      ctx.addIssue({
        code: "custom",
        message: REQUIRED,
        path: ["referralSourceExtra"]
      });
    } else if (extra.length < 2) {
      ctx.addIssue({
        code: "custom",
        message: MIN_DEFAULT("Referral source", 2),
        path: ["referralSourceExtra"]
      });
    }
  }
});

// schemas/requests/file.ts
import { z as z19 } from "zod";
var FileUploadFieldsSchema = z19.object({
  email: z19.string().email(),
  fullName: z19.string().optional(),
  userId: z19.string().optional()
});
var FileDeleteSchema = z19.object({
  email: z19.string().optional(),
  file_id: z19.string().optional()
});

// schemas/requests/recaptcha.ts
import { z as z20 } from "zod";
var RecaptchaRequestSchema = z20.object({
  token: z20.string().min(1)
});

// schemas/responses/auth.ts
import { z as z21 } from "zod";
var OkResponse = z21.object({ ok: z21.boolean() });
var StartOtpResponse = OkResponse;
var VerifyOtpResponse = z21.object({ user: DbUserSchema });
var MeResponse = z21.object({ user: DbUserSchema });

// schemas/responses/file.ts
import { z as z22 } from "zod";
var FileResponseSchema = z22.object({
  path: z22.url().nullable(),
  original: z22.string().nullable(),
  mime: z22.string().nullable(),
  size: z22.number().nullable()
});

// schemas/responses/contact.ts
import { z as z23 } from "zod";
var ContactResponseSchema = z23.object({ success: z23.boolean(), message: z23.string(), data: z23.any().optional() });

// schemas/responses/recaptcha.ts
import { z as z24 } from "zod";
var RecaptchaResponseSchema = z24.object({ success: z24.boolean(), message: z24.string() });

// __tests__/fixtures/makeUserDoc.ts
function makeUserDoc(overrides = {}) {
  const base = {
    _id: "user123",
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString(),
    email: "a@b.com",
    age: null,
    hubspot_id: null,
    application_status: { general: true, background: true, program: true, aid: true },
    application_process: "Draft",
    lastTimeSaved: (/* @__PURE__ */ new Date()).toISOString(),
    toObject() {
      const { toObject, save, ...rest } = this;
      return { ...rest };
    },
    // Plain async stub — tests should replace with a spy if they need to assert calls
    async save() {
      return true;
    }
  };
  return { ...base, ...overrides };
}
__name(makeUserDoc, "makeUserDoc");
export {
  ApplicationProcessValues,
  ContactResponseSchema,
  ContactTypes,
  DbUserSchema,
  ENGLISH_SPEAKING_COUNTRIES,
  FIELD_LIMITS,
  FileDeleteSchema,
  FileResponseSchema,
  FileUploadFieldsSchema,
  INVALID_PHONE,
  MISSING_PHONE_COUNTRY,
  MeResponse,
  OkResponse,
  REFERRAL_EXTRA_LABELS,
  REFERRAL_NEEDS_EXTRA,
  REFERRAL_NEEDS_EXTRA_SET,
  REFERRAL_OPTIONS,
  REQUIRED,
  RecaptchaRequestSchema,
  RecaptchaResponseSchema,
  StartOtpResponse,
  StartOtpSchema,
  VerifyOtpResponse,
  VerifyOtpSchema,
  WebEnvSchema,
  WorkflowProcessStatuses,
  ageFromCalendarDate,
  anyToCalendarDate,
  applicationUpdateSchema,
  calendarDateToMDY,
  calendarDatesToCalendarRangeString,
  calendarRangeStringToCalendarDates,
  capitalizeFirstLetter,
  computeAgeFromMDY,
  computeApplicationStatus,
  counselorRegistrationSchema,
  createBackgroundSchema,
  defaultApplicationProcess,
  defaultApplicationStatus,
  email,
  getCountries,
  getCountryCallingCode,
  getSummerBoundsCalendarDates,
  hasValidTld,
  isApplicationComplete,
  isCountryCode,
  isISO,
  isMDY,
  isValidPhone,
  isoToCalendarDate,
  makeUserDoc,
  mdyToCalendarDate,
  mdyToTimestamp,
  needsExtraFor,
  nextJune15,
  nextValidSummerYear,
  parentRegistrationSchema,
  parseApiEnv,
  parsePhone,
  parseSchoolDateRange,
  parseWebEnv,
  phoneNumberSchema,
  requiresVideoIntro,
  stepAidSchema,
  backgroundSchema as stepBackgroundSchema,
  stepGeneralSchema,
  programSchema as stepProgramSchema,
  straightenApostrophes,
  summerBounds,
  toE164,
  upcomingSummerYear
};
